<template>
  <b-card
    v-show="!errorContent.table"
    no-body
    class="mb-0"
  >

    <!-- Datos cargados -->
    <b-card-header class="pb-0">
      <b-card-title />
      <b-card-text class="font-small-2 mr-25 mb-0">
        Datos cargados. <b>{{ updatedAt }}</b>
      </b-card-text>
    </b-card-header>

    <!-- Table Top -->
    <div class="m-2">
      <b-row>
        <!-- Cantidad de elementos por página -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <span>Mostrar</span>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block mx-50"
          />
        </b-col>

        <!-- Buscador -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-input-group class="input-group-merge">
              <b-input-group-prepend is-text>
                <feather-icon
                  :class="{ 'text-danger cursor-pointer': searchQuery != '' }"
                  :icon="searchQuery != '' ? 'XIcon' : 'SearchIcon'"
                  @click="searchQuery != '' ? searchQuery = '' : ''"
                />
              </b-input-group-prepend>
              <b-form-input
                v-model="searchQuery"
                placeholder="Buscar"
              />
            </b-input-group>
          </div>
        </b-col>
      </b-row>
    </div>
    <b-overlay
      rounded="sm"
      :show="isBusyTable"
      opacity="0"
    >
      <b-table
        ref="refDataListTable"
        class="position-relative"
        empty-text="No se encontraron resultados"
        primary-key="id_table_alu_pos_baja"
        responsive
        show-empty
        striped
        :items="fetchData"
        :fields="tableColumns"
        :sort-desc.sync="isSortDirDesc"
        :sort-by.sync="sortBy"
      >
        <template #cell(tipo_solicitante)="data">
          <div v-if="data.item.tipo_solicitante === 'ALU'">
            Alumno
          </div>
          <div v-if="data.item.tipo_solicitante === 'CA'">
            Control Académico
          </div>
          <div v-if="data.item.tipo_solicitante === 'CE'">
            Control Escolar
          </div>
        </template>
        <template #cell(tipo_baja)="data">
          <b-avatar
            size="34"
            :text="data.item.tipo_baja"
            :variant="`light-${resolveTipoBajaVariant(data.item.tipo_baja)}`"
          />
          <small> - {{ resolveTipoNombre(data.item.tipo_baja) }}</small>
        </template>
        <template #cell(nombre)="data">
          <small> {{ data.item.nombre }} {{ data.item.segundoNombre }} {{ data.item.apellidoPaterno }} {{ data.item.apellidoMaterno }}</small>
        </template>
        <!-- Column: Opciones -->
        <template #cell(opciones)="data">
          <div class="text-nowrap text-center">
            <b-link
              class="font-weight-bold text-secondary"
              :href="`${delivery}${data.item.rutaDoc}${data.item.extBase}`"
              target="_blank"
            >
              <feather-icon
                :id="`archivo-${data.item.id}`"
                icon="DownloadIcon"
                class="cursor-pointer mr-1"
                size="16"
              />
              <b-tooltip
                placement="topleft"
                title="Documento"
                :target="`archivo-${data.item.id}`"
              />
            </b-link>
            <b-button
              v-b-modal="`modal-bajasm${data.item.id}`"
              variant="outline"
              pill
            >
              <feather-icon
                :id="`materias-afectadas-${data.item.id}`"
                icon="FileTextIcon"
                class="cursor-pointer mr-1"
                size="16"
              />
              <b-tooltip
                placement="topright"
                title="Materias"
                :target="`materias-afectadas-${data.item.id}`"
              />
            </b-button>
            <b-modal
              :id="`modal-bajasm${data.item.id}`"
              scrollable
              :title="`Materias - Periodo ${data.item.clave}`"
              size="lg"
              ok-only
              ok-title="Cerrar"
              button-size="sm"
            >
              <materias
                :ag="data.item.agenda"
                :mat="data.item.matricula"
                :nombre="`${data.item.nombre} ${data.item.segundoNombre} ${data.item.apellidoPaterno} ${data.item.apellidoMaterno}`"
              />
            </b-modal>
          </div>
        </template>
        <template #cell(editar)="data">
          <div
            v-if="aCAeditarsol"
            class="text-nowrap text-center"
          >
            <b-button
              v-b-modal="`modal-edit${data.item.id}`"
              variant="outline"
              pill
            >
              <feather-icon
                :id="`${data.item.id}`"
                icon="EditIcon"
                class="cursor-pointer mr-1"
                size="16"
              />
            </b-button>
            <b-modal
              :id="`modal-edit${data.item.id}`"
              scrollable
              :title="`Editar Solicitud - Periodo ${data.item.clave}`"
              size="xl"
              ok-only
              ok-title="Cerrar"
              button-size="sm"
            >
              <component
                :is="aCAeditarsol"
                ref="editarsol"
                :sol="data.item.id"
                :estado="data.item.resolucion"
                :tipo="data.item.tipo_baja"
                :mat="data.item.matricula"
                :ag="data.item.agenda"
                :nombre="`${data.item.nombre} ${data.item.segundoNombre} ${data.item.apellidoPaterno} ${data.item.apellidoMaterno}`"
                @res="resedit=$event"
              />
            </b-modal>
          </div>
        </template>
        <template #cell(resolucion)="data">
          <b-media vertical-align="center">
            <b-badge
              :variant="`${resolveTipoEstado(data.item.resolucion)}`"
              pill
              class="badge-round"
            > <small>{{ resolveEstadoNombre(data.item.resolucion) }}</small>
            </b-badge>
          </b-media>
        </template>
      </b-table>
    </b-overlay>
    <!-- Table's footer & pagination -->
    <div class="mx-2 mb-2">
      <b-row>
        <!-- Leyenda de cuantos elementos se muestran -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">Mostrando {{ dataMeta.from }} a {{ dataMeta.to }} de {{ dataMeta.of }} registros</span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalItems"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>

        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BOverlay, BCardHeader, BCardTitle, BCardText, BRow, BCol,
  BFormInput, BInputGroup, BInputGroupPrepend, BPagination, BMedia, BBadge,
  BLink, BTooltip, BAvatar, VBModal, BModal, BButton,

} from 'bootstrap-vue'
import { useFillDataTable } from '@core/components/data-table/useFillDataTable'
import {
  avatarText, lastUpdate,
} from '@core/utils/filter'
import {
  ref, onMounted, watch, computed,
} from '@vue/composition-api'
import store from '@/store'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import materias from './contenidoModalMaterias.vue'
import { configTableAlumnosBajas } from './useBaja'

const editarsol = () => import('./contenidoModalEditarSolicitud.vue')

export default {
  components: {
    BCard,
    BTable,
    BOverlay,
    BCardHeader,
    BCardTitle,
    BCardText,
    BRow,
    BCol,
    vSelect,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BPagination,
    BMedia,
    BBadge,
    BLink,
    BTooltip,
    BAvatar,
    BModal,
    materias,
    BButton,
    editarsol,
  },
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  setup() {
    const delivery = process.env.VUE_APP_DELIVERY_DOWNLOAD
    const aCAeditarsol = ref(null)
    const resedit = ref(null)
    const userRole = computed(() => store.state.cuenta.rolSelected)

    const {
      //* Fetching data
      fetchData, refetchData, refDataListTable,
      //* Pagination
      dataMeta,
      //* Table dynamic params
      dynamicParamRequest,
      //* Basic filters & pagination
      perPage, totalItems, currentPage, perPageOptions, searchQuery, sortBy, isSortDirDesc,
      //* Loader
      isBusyTable,
      //* Error
      errorRequest,
    } = useFillDataTable()
    const updatedAt = ref(lastUpdate())
    const {
      tableColumns,
      configRequest,
      resolveTipoBajaVariant,
      resolveTipoNombre,
      resolveTipoEstado,
      resolveEstadoNombre,
    } = configTableAlumnosBajas()

    const errorContent = ref({ filters: false, table: false })

    //* On mounted
    onMounted(() => {
      dynamicParamRequest.value = configRequest.value //* Asign config params for each module as dynamic params
      aCAeditarsol.value = userRole.value.id === 10 ? 'editarsol' : null
    })

    watch(errorRequest, error => { if (error) errorContent.value.table = error }) //* If the flag 'errorRequest' from data request is true, hide table content
    watch(configRequest.value, () => { refetchData() }) //* Any change in config params (filters), refetch table data
    watch(() => resedit.value, valor => {
      if (valor === true) refetchData(true)
    })
    return {
      fetchData,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDataListTable,
      refetchData,
      avatarText,
      isBusyTable,
      configRequest,
      resolveTipoBajaVariant,
      resolveTipoNombre,
      resolveTipoEstado,
      resolveEstadoNombre,
      errorContent,
      updatedAt,
      delivery,
      aCAeditarsol,
      resedit,
    }
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
